import React from 'react';
import { motion } from 'framer-motion';

const BuildingTypeSelector = ({ selectedType, onSelect, onContinue }) => {
  const buildingTypes = [
    {
      id: 'residential',
      title: 'Residential',
      description: 'Perfect for apartments and homes',
      icon: 'home',
      image: "https://res.cloudinary.com/dknsjvavr/image/upload/v1753253374/residential-building_v2t9yi.jpg",
    },
    {
      id: 'commercial',
      title: 'Commercial',
      description: 'Ideal for offices and retail spaces',
      icon: 'building',
      image: "https://res.cloudinary.com/dknsjvavr/image/upload/v1753253374/commercial-building_ewvygc.jpg"
    },
    {
      id: 'hospital',
      title: 'Hospital',
      description: 'Specialized for healthcare facilities',
      icon: 'hospital',
      image: "https://res.cloudinary.com/dknsjvavr/image/upload/v1753253374/hospital-building_otxcuc.jpg"
    },
    {
      id: 'hotel',
      title: 'Hotel',
      description: 'Luxurious options for hospitality',
      icon: 'hotel',
      image: "https://res.cloudinary.com/dknsjvavr/image/upload/v1753253376/hotel-building_v96lfu.png"
    },
    {
      id: 'factory',
      title: 'Industrial',
      description: 'Heavy duty for factories and warehouses',
      icon: 'industry',
      image: "https://res.cloudinary.com/dknsjvavr/image/upload/v1753253376/industrial-building_ucgjb4.png"
    },
    {
      id: 'other',
      title: 'Other',
      description: 'Custom solutions for special needs',
      icon: 'building-shield',
      image: "https://res.cloudinary.com/dknsjvavr/image/upload/v1753253376/other-building_gnrcxz.png"
    }
  ];

  const isValidSelection = selectedType !== '';

  return (
    <div style={styles.container}>
      <p style={styles.intro}>
        Select the building type where your elevator will be installed. This helps us recommend the most suitable options for your project.
      </p>
      
      <div style={styles.typeGrid}>
        {buildingTypes.map(type => (
          <motion.div
            key={type.id}
            style={{
              ...styles.typeCard,
              ...(selectedType === type.id ? styles.selectedCard : {})
            }}
            whileHover={{ 
              y: -5, 
              boxShadow: '0 12px 30px rgba(0,0,0,0.15)' 
            }}
            onClick={() => onSelect(type.id)}
            transition={{ duration: 0.2 }}
            tabIndex={0}
            role="button"
            aria-pressed={selectedType === type.id}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onSelect(type.id);
                e.preventDefault();
              }
            }}
          >
            <div style={styles.cardImageContainer}>
              <div style={styles.cardImageOverlay}></div>
              <img 
                src={type.image} 
                alt={`${type.title} building`} 
                style={styles.cardImage} 
                loading="lazy"
              />
              {selectedType === type.id && (
                <motion.div 
                  style={styles.checkmarkOverlay}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    style={styles.checkmarkContainer}
                  >
                    <i className="fas fa-check" style={styles.checkmark}></i>
                  </motion.div>
                </motion.div>
              )}
            </div>
            
            <div style={styles.cardContent}>
              <div style={styles.iconWrapper}>
                <i className={`fas fa-${type.icon}`} style={styles.cardIcon}></i>
              </div>
              <h3 style={styles.cardTitle}>{type.title}</h3>
              <p style={styles.cardDescription}>{type.description}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <div style={styles.actionContainer}>
        <motion.button
          style={{
            ...styles.continueButton,
            ...(isValidSelection ? {} : styles.disabledButton)
          }}
          whileHover={isValidSelection ? { scale: 1.03 } : {}}
          whileTap={isValidSelection ? { scale: 0.98 } : {}}
          onClick={isValidSelection ? onContinue : undefined}
          disabled={!isValidSelection}
          aria-disabled={!isValidSelection}
        >
          Continue to Floors Selection
          <i className="fas fa-arrow-right" style={styles.arrowIcon}></i>
        </motion.button>
        
        {!isValidSelection && (
          <p style={styles.selectionHint}>Please select a building type to continue</p>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px 0',
  },
  intro: {
    fontSize: '18px',
    color: '#4d4d4d',
    textAlign: 'center',
    maxWidth: '700px',
    margin: '0 auto 40px auto',
    lineHeight: '1.6',
  },
  typeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '30px',
    padding: '10px 0',
    maxWidth: '1100px',
    margin: '0 auto',
    '@media (max-width: 600px)': {
      gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
      gap: '20px',
    },
  },
  typeCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    border: '1px solid #eaeaea',
    '&:focus': {
      outline: 'none',
      boxShadow: '0 0 0 3px rgba(22, 59, 162, 0.3), 0 4px 20px rgba(0,0,0,0.08)',
    },
    '&:hover': {
      borderColor: '#d0d0d0',
    },
  },
  selectedCard: {
    boxShadow: '0 0 0 2px #163ba2, 0 10px 20px rgba(0,0,0,0.1)',
    borderColor: '#163ba2',
  },
  cardImageContainer: {
    position: 'relative',
    height: '380px',
    overflow: 'hidden',
  },
  cardImageOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.1)',
    zIndex: 1,
  },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'stretch',
    transition: 'transform 0.7s ease',
  },
  checkmarkOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(22, 59, 162, 0.75)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
  },
  checkmarkContainer: {
    backgroundColor: 'white',
    borderRadius: '50%',
    width: '50px',
    height: '50px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
  },
  checkmark: {
    color: '#163ba2',
    fontSize: '24px',
  },
  cardContent: {
    padding: '24px',
    position: 'relative',
  },
  iconWrapper: {
    backgroundColor: '#f4f7fd',
    borderRadius: '12px',
    width: '40px',
    height: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '16px',
  },
  cardIcon: {
    color: '#163ba2',
    fontSize: '18px',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1d1d1f',
    margin: '0 0 8px 0',
    lineHeight: '1.3',
  },
  cardDescription: {
    fontSize: '15px',
    color: '#636366',
    margin: '0',
    lineHeight: '1.5',
  },
  actionContainer: {
    marginTop: '50px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  continueButton: {
    backgroundColor: '#163ba2',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '16px 36px',
    fontSize: '17px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(22, 59, 162, 0.25)',
    outline: 'none',
    '&:focus': {
      boxShadow: '0 0 0 3px rgba(22, 59, 162, 0.3), 0 4px 12px rgba(22, 59, 162, 0.25)',
    },
  },
  disabledButton: {
    backgroundColor: '#a0a0a0',
    cursor: 'not-allowed',
    opacity: 0.7,
    boxShadow: 'none',
  },
  arrowIcon: {
    marginLeft: '12px',
    fontSize: '16px',
  },
  selectionHint: {
    color: '#8e8e93',
    fontSize: '14px',
    marginTop: '12px',
    fontStyle: 'italic',
  }
};

export default BuildingTypeSelector;